export const DEFAULT_PATH = '/ws/session';

/**
 * Ventana de silencio del mic tras el FIN ACÚSTICO del TTS. Defensa final
 * después del `playerRef.current.isPlaying()` gate del handler de pcm:
 *   - Decay del AEC del browser para "olvidar" el audio recién reproducido
 *     (~150-200ms típico Chrome/Safari).
 *   - Reverb del ambiente físico (parlantes laptop, speakerphone iPhone) —
 *     el audio salió del AudioContext pero todavía rebota por la pieza.
 * Se arma desde dos puntos: (a) `awaitIdle()` resolviendo en `turn-end` —
 * el path normal, alineado al fin acústico real; (b) transición de state
 * speaking/thinking → listening — fallback por si `awaitIdle` no corre.
 * Trade-off real: si el user empieza a hablar dentro de esa ventana, esos
 * primeros MIC_SETTLE_MS de audio NO llegan al STT (preferimos perder un
 * fragmento a alucinar un turno entero por eco).
 */
export const MIC_SETTLE_MS = 300;

/**
 * Mínimo de caracteres alfanuméricos en un partial transcript para que
 * llegue a la UI. Filtra basura tipo ".", "...", "uh", letras sueltas
 * que llegan cuando el STT abre una utterance especulativa sobre
 * ruido/eco residual. Final transcripts NUNCA se filtran — el server ya
 * commiteó el turno.
 */
export const MIN_PARTIAL_CHARS = 2;

/**
 * Auto-reconexión tras una caída INESPERADA del WS (restart del backend, blip
 * de red). El cierre intencional (`stop()`/unmount) NO dispara reintentos.
 *
 * Cada reintento reabre el WS pasando `resumeSessionId` = el `sessionId` de la
 * sesión caída, así el backend rehidrata los últimos turnos persistidos
 * (`loadResumeMessages`) y el coach conserva el contexto. El mic + player se
 * mantienen vivos entre intentos (MicCapture es singleton idempotente), así
 * que no se re-pide permiso ni se reinicializa el AudioContext.
 */
export const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Close code con el que el backend corta la sesión por SALDO AGOTADO: lo usan
 * el preflight de arranque (`SessionStartFlow/phases/preflightCredits.ts` →
 * `createOnVoiceConnection.ts`) y el fusible de overdraft mid-sesión
 * (`SessionStartFlow/phases/wireOverdraftFuse.ts`). Los dos mandan un
 * `session.blocked` antes de cerrar.
 *
 * El close handler lo trata como cierre DELIBERADO: reconectar contra un
 * backend que ya dijo que no gasta el presupuesto de reintentos y termina en
 * `lost`, o sea el user ve "conexión perdida" en vez del copy de saldo. El
 * mensaje `session.blocked` marca lo mismo un instante antes (y es la ÚNICA
 * vía para el 4403 del safety L4 de coach), pero los dos emisores lo mandan
 * dentro de un `try/catch` con el WS ya cerrándose: si ese `send` falla, el
 * código de cierre es lo único que llega.
 */
export const WS_CLOSE_INSUFFICIENT_CREDITS = 4402;

/**
 * Cuánto tiempo debe permanecer ABIERTO un socket para considerar la conexión
 * ESTABLE y recién ahí resetear el presupuesto de reintentos a 0.
 *
 * Por qué no resetear en el `open` a secas: un socket que abre y se cae a los
 * pocos ms (sesión que muere apenas arranca, tab en background que el browser
 * suspende, blip de red persistente) cuenta como "conexión establecida" en el
 * evento `open`. Si reseteáramos el contador ahí, cada ciclo abrir→morir
 * reiniciaría el presupuesto y la reconexión NUNCA agotaría los intentos → loop
 * infinito (cada ciclo, además, arma una sesión nueva y su saludo). Exigir que
 * el socket sobreviva esta ventana antes de resetear convierte una conexión que
 * "flapea" en un `lost` acotado (banner + reconexión manual), que es el UX
 * previsto para una conexión inestable.
 */
export const STABLE_CONNECTION_MS = 8_000;

/**
 * Ya en `lost` (ráfaga de reintentos agotada), el hook sigue intentando SOLO:
 * cada `LOST_AUTO_RETRY_MS` mientras el tab esté visible, y de inmediato cuando
 * el browser avisa que volvió la red (`online`) o que el user volvió al tab
 * (`visibilitychange`). Sin esto, una caída de 30 segundos dejaba la charla
 * muerta hasta que alguien tocara "Reconectar" a mano.
 *
 * El intervalo es LARGO a propósito: cada auto-reintento vuelve a gastar la
 * ráfaga completa de `MAX_RECONNECT_ATTEMPTS` (~15s) y, si el backend contesta
 * y se cae enseguida, cada ciclo arma una sesión nueva con su saludo. El tope
 * `MAX_LOST_AUTO_RETRIES` acota el desperdicio de un backend caído por horas;
 * las señales `online`/`visible` reponen el presupuesto porque son información
 * NUEVA (la red volvió / el user está mirando), no un reintento a ciegas.
 */
export const LOST_AUTO_RETRY_MS = 15_000;
export const MAX_LOST_AUTO_RETRIES = 8;

/** Backoff exponencial: delay = min(BASE * 2^intento, MAX). Sin jitter (tests deterministas). */
export const RECONNECT_BASE_DELAY_MS = 500;
export const RECONNECT_MAX_DELAY_MS = 8_000;

/** Delay del intento n (0-indexed). Exportado para testear el backoff. */
export function reconnectDelayMs(attempt: number): number {
  return Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
}
